<script setup lang="ts">
import { FeatureMainMenu } from "~/features/main-menu";
import { FeatureSearch } from "~/features/search";
import { FeatureFavoriteCountBadge } from "~/features/favorites";
import { FeatureCartTrigger } from "~/features/cart";
import { FeatureAuthTrigger } from "~/features/auth";

const { position = "fixed" } = defineProps<{
    position?: "fixed" | "sticky" | "static";
}>();

const isHeaderVisible = ref(true);
const lastScrollY = ref(0);
const route = useRoute();

const TOP_OFFSET = 40;
const TOGGLE_THRESHOLD = 12;

const headerTranslateY = computed(() =>
    isHeaderVisible.value ? "0%" : "-100%",
);
const headerOpacity = computed(() => (isHeaderVisible.value ? "1" : "0"));
const headerPointerEvents = computed(() =>
    isHeaderVisible.value ? "auto" : "none",
);

const getScrollY = () => {
    if (import.meta.client) {
        return Math.max(window.scrollY, 0);
    }

    return 0;
};

const syncHeaderState = () => {
    const currentScrollY = getScrollY();

    lastScrollY.value = currentScrollY;
    isHeaderVisible.value = currentScrollY <= TOP_OFFSET;
};

const onScroll = () => {
    const currentScrollY = getScrollY();
    const delta = currentScrollY - lastScrollY.value;

    if (currentScrollY <= TOP_OFFSET) {
        isHeaderVisible.value = true;
        lastScrollY.value = currentScrollY;
        return;
    }

    if (delta > TOGGLE_THRESHOLD) {
        isHeaderVisible.value = false;
    } else if (delta < -TOGGLE_THRESHOLD) {
        isHeaderVisible.value = true;
    }

    lastScrollY.value = currentScrollY;
};

onMounted(() => {
    syncHeaderState();
});

useEventListener(import.meta.client ? window : undefined, "scroll", onScroll, {
    passive: true,
});

watch(
    () => route.fullPath,
    () => {
        syncHeaderState();
    },
    {
        immediate: true,
    },
);

const client = useStrapiClient();
const cachePublicRequest = usePublicDataRequest();
await useAsyncData(
    "main-menu",
    () => cachePublicRequest(
        ["strapi", "main-menu"],
        () => client.single("navigation/render/main-menu?type=TREE")
            .find() as unknown as Promise<NavigationMenu>,
        300,
    ),
);
</script>
<template>
    <div
        class="left-0 top-0 z-[42] w-full"
        :class="{
            fixed: position === 'fixed',
            sticky: position === 'sticky',
            static: position === 'static',
            'ui-header-overlay': position === 'sticky',
        }"
    >
        <div class="ui-header bg-transparent w-full flex justify-center">
            <div
                class="ui-header-content container px-4 items-center grid grid-cols-[1fr_auto_1fr]"
            >
                <FeatureMainMenu />

                <NuxtLink to="/" class="mx-auto" aria-label="На главную">
                    <SvgoLogo
                        aria-hidden="true"
                        class="h-8 lg:h-11 mx-auto"
                        :fontControlled="false"
                    />
                </NuxtLink>
                <div class="flex items-center justify-self-end gap-2 lg:gap-3">
                    <FeatureSearch />
                    <FeatureAuthTrigger class="size-6" />
                    <FeatureCartTrigger
                        class="flex size-6 cursor-pointer items-center justify-center"
                    />
                    <NuxtLink no-prefetch
                        to="/favorites"
                        class="relative flex size-6 items-center justify-center"
                        aria-label="Избранное"
                    >
                        <SvgoHeart
                            aria-hidden="true"
                            filled
                            class="!mb-0 text-2xl"
                        />
                        <ClientOnly>
                            <FeatureFavoriteCountBadge />
                        </ClientOnly>
                    </NuxtLink>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.ui-header {
    background-color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 4px 10px 0 hsla(0,0%,29%,0.15);
    z-index: 42;
    transition: background 0.3s linear, color 0.3s linear, box-shadow 0.3s linear;
}
.ui-header:hover {
    background-color: rgba(255, 255, 255, 1);

}
.ui-header-content {
    padding-top: calc(0.5rem + env(safe-area-inset-top, 0px));
    padding-bottom: 0.5rem;
}

.ui-header-overlay {
    height: 0;
    min-height: 0;
}

@media (min-width: 1024px) {
    .ui-header-content {
        padding-top: calc(0.8125rem + env(safe-area-inset-top, 0px));
        padding-bottom: 0.8125rem;
    }

}

.ui-header-shell {
    transform: translate3d(0, v-bind(headerTranslateY), 0);
    opacity: v-bind(headerOpacity);
    pointer-events: v-bind(headerPointerEvents);
    transition:
        background-color 200ms ease,
        transform 200ms ease,
        opacity 200ms ease;
    will-change: transform, opacity;
}

body:has(.search-dialog[data-state="open"]) .ui-header {
    background-image: unset !important;
    color: black !important;
}
</style>
